import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Ear, Headphones, Upload, Play, Trash2, Camera, Video, Mic, Stethoscope, X, FileText, Sparkles, MicOff } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, API, getToken, formatApiError } from "@/lib/api";

const OTO_SITES = [
  { value: "left_ear", label: "Left Ear" },
  { value: "right_ear", label: "Right Ear" },
  { value: "throat", label: "Throat" },
  { value: "skin", label: "Skin" },
];

const STETH_HEART_SITES = [
  { value: "apex", label: "Apex" },
  { value: "aortic", label: "Aortic" },
  { value: "pulmonic", label: "Pulmonic" },
  { value: "tricuspid", label: "Tricuspid" },
];

const STETH_LUNG_SITES = [
  { value: "lung_upper_left", label: "Upper Left" },
  { value: "lung_upper_right", label: "Upper Right" },
  { value: "lung_lower_left", label: "Lower Left" },
  { value: "lung_lower_right", label: "Lower Right" },
];

function siteLabel(v) {
  const all = [...OTO_SITES, ...STETH_HEART_SITES, ...STETH_LUNG_SITES];
  return all.find((s) => s.value === v)?.label || v;
}

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function fmtSize(n) {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function mediaUrl(patientId, rec) {
  const token = getToken();
  return `${API}/patients/${patientId}/recordings/${rec.id}/media?auth=${encodeURIComponent(token || "")}`;
}

function VoiceDictate({ onTranscribed }) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);

  async function start() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setBusy(true);
        try {
          const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
          const fd = new FormData(); fd.append("file", blob, "dictation.webm");
          const r = await api.post("/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
          onTranscribed && onTranscribed(r.data.text || "");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
      };
      recRef.current = mr;
      mr.start();
      setRecording(true);
    } catch (e) {
      toast.error("Microphone access denied.");
    }
  }
  function stop() {
    try { recRef.current?.stop(); } catch (_e) { /* noop */ }
    setRecording(false);
  }
  return (
    <button type="button" onClick={recording ? stop : start} disabled={busy}
      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border ${
        recording ? "border-red-300 text-red-600 bg-red-50" : "border-[#0B7E8C]/30 text-[#0B7E8C] hover:bg-[#0B7E8C]/5"
      }`}
      data-testid="voice-dictate-btn">
      {busy ? "Transcribing…" : recording ? (<><MicOff className="w-3 h-3" /> Stop</>) : (<><Mic className="w-3 h-3" /> Dictate</>)}
    </button>
  );
}

function UploadDialog({ open, onOpenChange, patientId, device, onUploaded }) {
  const [site, setSite] = useState("");
  const [region, setRegion] = useState(device === "stethoscope" ? "heart" : "");  // stethoscope only
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [visits, setVisits] = useState([]);
  const [linkedVisitId, setLinkedVisitId] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) { setSite(""); setRegion(device === "stethoscope" ? "heart" : ""); setNotes(""); setFile(null); setLinkedVisitId(""); }
  }, [open, device]);

  useEffect(() => {
    if (!open) return;
    // Load the patient's open/recent visits so the clinician can (optionally) link the recording.
    api.get(`/fleet/visits`, { params: { patient_id: patientId, page_size: 20 } })
      .then((r) => setVisits((r.data.items || r.data.visits || []).filter((v) => ["assigned", "accepted", "en_route", "in_progress", "scheduled", "unassigned"].includes(v.status))))
      .catch(() => setVisits([]));
  }, [open, patientId]);

  const siteOptions = device === "otoscope"
    ? OTO_SITES
    : (region === "heart" ? STETH_HEART_SITES : STETH_LUNG_SITES);

  const accept = device === "otoscope"
    ? "image/png,image/jpeg,image/webp,video/mp4,video/webm,video/quicktime"
    : "audio/wav,audio/mpeg,audio/mp4,audio/webm,audio/ogg,audio/aac,audio/x-wav";

  async function submit() {
    if (!site) { toast.error("Please pick a body site."); return; }
    if (!file) { toast.error("Please select a file to upload."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("device_type", device);
      fd.append("body_site", site);
      if (notes.trim()) fd.append("notes", notes.trim());
      if (linkedVisitId) fd.append("linked_visit_id", linkedVisitId);
      fd.append("file", file);
      await api.post(`/patients/${patientId}/recordings`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Recording uploaded — AI hint generating in the background");
      onUploaded && onUploaded();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`upload-${device}-dialog`} className="max-w-lg">
        <DialogHeader><DialogTitle className="capitalize">Upload {device} recording</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {device === "stethoscope" && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5 font-semibold">Body region</div>
              <div className="flex gap-2">
                {["heart", "lungs"].map((r) => (
                  <button
                    key={r}
                    onClick={() => { setRegion(r); setSite(""); }}
                    className={`flex-1 px-3 py-2 rounded-lg border text-sm font-medium capitalize transition-colors ${
                      region === r ? "border-[#0B7E8C] bg-[#0B7E8C]/5 text-[#0B7E8C]" : "border-[#E5E7EB] text-[#6B7280] hover:border-[#0B7E8C]/50"
                    }`}
                    data-testid={`upload-region-${r}`}
                  >{r}</button>
                ))}
              </div>
            </div>
          )}
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5 font-semibold">
              {device === "otoscope" ? "Body site" : "Auscultation position"}
            </div>
            <div className="flex flex-wrap gap-2">
              {siteOptions.map((s) => (
                <button
                  key={s.value}
                  onClick={() => setSite(s.value)}
                  className={`px-3 py-1.5 rounded-full border text-sm font-medium transition-colors ${
                    site === s.value ? "border-[#0B7E8C] bg-[#0B7E8C]/10 text-[#0B7E8C]" : "border-[#E5E7EB] text-[#6B7280] hover:border-[#0B7E8C]/50"
                  }`}
                  data-testid={`upload-site-${s.value}`}
                >{s.label}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5 font-semibold">File</div>
            <input ref={fileRef} type="file" accept={accept} onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="text-sm w-full" data-testid={`upload-${device}-file`} />
            {file && <div className="text-[11px] text-[#9CA3AF] mt-1">{file.name} · {fmtSize(file.size)}</div>}
            <div className="text-[11px] text-[#9CA3AF] mt-1">
              {device === "otoscope"
                ? "PNG/JPG/WEBP (≤10 MB) or MP4/WEBM/MOV (≤100 MB)."
                : "WAV/MP3/M4A/WEBM/OGG/AAC (≤25 MB)."}
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-semibold">Annotation (optional)</div>
              <VoiceDictate onTranscribed={(t) => setNotes((prev) => (prev ? (prev.trim() + " " + t).trim() : t))} />
            </div>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Clinical impression, key findings, follow-up plan… or dictate with the mic."
              data-testid={`upload-${device}-notes`} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-1.5 font-semibold">
              Link to a home visit (optional)
            </div>
            <select
              value={linkedVisitId}
              onChange={(e) => setLinkedVisitId(e.target.value)}
              className="w-full text-sm px-3 py-2 rounded-md border border-[#E5E7EB] bg-white"
              data-testid={`upload-${device}-visit`}
            >
              <option value="">— Not linked to a visit —</option>
              {visits.map((v) => (
                <option key={v.id} value={v.id}>
                  {(v.visit_type || "visit").toUpperCase()} · {v.scheduled_at ? new Date(v.scheduled_at).toLocaleString() : "no date"} · {v.status}
                </option>
              ))}
            </select>
            <div className="text-[11px] text-[#9CA3AF] mt-1">
              Linking lets field teams see this recording on the Fleet visit detail. Leave unlinked for ad-hoc uploads.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
            data-testid={`upload-${device}-submit`}>
            <Upload className="w-4 h-4 mr-1.5" /> {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PreviewDialog({ open, onOpenChange, patientId, rec }) {
  if (!rec) return null;
  const src = mediaUrl(patientId, rec);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="preview-dialog" className="max-w-2xl">
        <DialogHeader><DialogTitle className="capitalize">
          {rec.device_type} · {siteLabel(rec.body_site)}
        </DialogTitle></DialogHeader>
        <div className="text-[11px] text-[#9CA3AF]">
          Recorded {fmtDate(rec.recorded_at)} by {rec.recorded_by_name}
        </div>
        <div className="mt-3 rounded-lg overflow-hidden bg-black flex items-center justify-center min-h-[240px]">
          {rec.media_type === "image" && <img src={src} alt="" className="max-h-[70vh] max-w-full" />}
          {rec.media_type === "video" && (
            <video src={src} controls playsInline className="max-h-[70vh] max-w-full" data-testid="preview-video" />
          )}
          {rec.media_type === "audio" && (
            <audio src={src} controls className="w-full my-8" data-testid="preview-audio" />
          )}
        </div>
        {rec.notes && (
          <div className="mt-3 rounded-md bg-slate-50 border border-slate-100 p-3 text-sm text-[#374151]">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-[#6B7280] mb-1 font-semibold">
              <FileText className="w-3.5 h-3.5" /> Annotation
            </div>
            {rec.notes}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RecordingCard({ patientId, rec, onPreview, onDelete, canDelete, onRegenerate, canRegenerate }) {
  const src = mediaUrl(patientId, rec);
  return (
    <div className="hah-card p-3 flex gap-3" data-testid={`recording-card-${rec.id}`}>
      <button
        onClick={() => onPreview(rec)}
        className="shrink-0 w-28 h-20 rounded-md overflow-hidden bg-slate-100 flex items-center justify-center hover:opacity-90"
        title="Preview"
      >
        {rec.media_type === "image" && <img src={src} alt="" className="w-full h-full object-cover" />}
        {rec.media_type === "video" && (
          <div className="text-slate-500 flex flex-col items-center"><Video className="w-6 h-6" /><span className="text-[10px] mt-1">Video</span></div>
        )}
        {rec.media_type === "audio" && (
          <div className="text-slate-500 flex flex-col items-center"><Mic className="w-6 h-6" /><span className="text-[10px] mt-1">Audio</span></div>
        )}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-[#1F2937]">{siteLabel(rec.body_site)}</span>
          <span className="inline-flex items-center rounded-full bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider">
            {rec.media_type}
          </span>
          {rec.linked_visit_id && (
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
              data-testid={`recording-visit-chip-${rec.id}`}>
              Visit linked
            </span>
          )}
          {rec.ai_hint && (
            <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 text-violet-700 border border-violet-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
              title={rec.ai_hint}
              data-testid={`recording-ai-chip-${rec.id}`}>
              AI hint
            </span>
          )}
          <span className="text-[11px] text-[#9CA3AF]">{fmtSize(rec.size_bytes)}</span>
        </div>
        <div className="text-[11px] text-[#9CA3AF] mt-0.5">
          {fmtDate(rec.recorded_at)} · {rec.recorded_by_name}
        </div>
        {rec.notes && <div className="text-sm text-[#374151] mt-1.5 line-clamp-2">{rec.notes}</div>}
        {rec.ai_hint && (
          <div className="mt-1.5 rounded-md bg-violet-50 border border-violet-100 text-[12px] text-violet-900 p-2 line-clamp-3"
            data-testid={`recording-ai-hint-${rec.id}`}>
            {rec.ai_hint}
          </div>
        )}
      </div>
      <div className="shrink-0 flex flex-col gap-1.5">
        <Button size="sm" variant="outline" onClick={() => onPreview(rec)} className="h-8"
          data-testid={`recording-preview-${rec.id}`}>
          <Play className="w-3.5 h-3.5 mr-1" /> View
        </Button>
        {canRegenerate && (
          <Button size="sm" variant="outline" onClick={() => onRegenerate(rec)}
            className="h-8 border-violet-200 text-violet-700 hover:bg-violet-50"
            title="Regenerate the AI hint using the current annotation"
            data-testid={`recording-regen-${rec.id}`}>
            <Sparkles className="w-3.5 h-3.5" />
          </Button>
        )}
        {canDelete && (
          <Button size="sm" variant="outline" onClick={() => onDelete(rec)}
            className="h-8 border-red-200 text-red-600 hover:bg-red-50"
            data-testid={`recording-delete-${rec.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

export default function PatientRecordingsTab({ patientId, device, canUpload, isAdmin }) {
  const [items, setItems] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewRec, setPreviewRec] = useState(null);
  const [siteFilter, setSiteFilter] = useState("");

  const load = useCallback(() => {
    api.get(`/patients/${patientId}/recordings`, { params: { device_type: device } })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => { toast.error(formatApiError(e)); setItems([]); });
  }, [patientId, device]);
  useEffect(() => { load(); }, [load]);

  async function onDelete(rec) {
    if (!window.confirm("Delete this recording? This cannot be undone from the app.")) return;
    try {
      await api.delete(`/patients/${patientId}/recordings/${rec.id}`);
      toast.success("Recording deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function onRegenerate(rec) {
    try {
      await api.post(`/patients/${patientId}/recordings/${rec.id}/regenerate-hint`);
      toast.success("AI hint regenerating…");
      // Reload after ~4s to pick up the new hint
      setTimeout(load, 4000);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const siteOptions = device === "otoscope"
    ? OTO_SITES
    : [...STETH_HEART_SITES, ...STETH_LUNG_SITES];

  const filtered = useMemo(() => (items || []).filter((r) => !siteFilter || r.body_site === siteFilter), [items, siteFilter]);

  const Icon = device === "otoscope" ? Ear : Headphones;
  const label = device === "otoscope" ? "Otoscope" : "Digital Stethoscope";

  return (
    <div data-testid={`recordings-tab-${device}`}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 text-base font-semibold text-[#1F2937]">
            <Icon className="w-5 h-5 text-[#0B7E8C]" /> {label} recordings
          </div>
          <div className="text-xs text-[#6B7280] mt-0.5">
            Historical {device === "otoscope" ? "images and videos" : "audio recordings"} captured from the mobile app or uploaded here.
          </div>
        </div>
        {canUpload && (
          <Button onClick={() => setUploadOpen(true)} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
            data-testid={`recordings-upload-btn-${device}`}>
            <Upload className="w-4 h-4 mr-1.5" /> Upload recording
          </Button>
        )}
      </div>

      {/* Site filter chips */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setSiteFilter("")}
          className={`px-3 py-1 rounded-full border text-[12px] font-medium transition-colors ${
            !siteFilter ? "border-[#0B7E8C] bg-[#0B7E8C]/10 text-[#0B7E8C]" : "border-[#E5E7EB] text-[#6B7280] hover:border-[#0B7E8C]/50"
          }`}
          data-testid={`recordings-filter-all-${device}`}
        >All ({(items || []).length})</button>
        {siteOptions.map((s) => {
          const count = (items || []).filter((r) => r.body_site === s.value).length;
          return (
            <button
              key={s.value}
              onClick={() => setSiteFilter(s.value)}
              className={`px-3 py-1 rounded-full border text-[12px] font-medium transition-colors ${
                siteFilter === s.value ? "border-[#0B7E8C] bg-[#0B7E8C]/10 text-[#0B7E8C]" : "border-[#E5E7EB] text-[#6B7280] hover:border-[#0B7E8C]/50"
              }`}
              data-testid={`recordings-filter-${s.value}-${device}`}
            >{s.label} ({count})</button>
          );
        })}
      </div>

      {items === null ? (
        <div className="text-sm text-[#6B7280]" data-testid={`recordings-loading-${device}`}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="hah-card p-8 text-center" data-testid={`recordings-empty-${device}`}>
          <Icon className="w-10 h-10 mx-auto text-[#D1D5DB] mb-2" />
          <div className="text-sm text-[#6B7280]">No {device} recordings yet.</div>
          {canUpload && (
            <div className="text-[11px] text-[#9CA3AF] mt-1">
              Recordings taken on the mobile app will appear here automatically, or upload manually.
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {filtered.map((rec) => (
            <RecordingCard key={rec.id} patientId={patientId} rec={rec}
              onPreview={setPreviewRec} onDelete={onDelete} canDelete={isAdmin}
              onRegenerate={onRegenerate} canRegenerate={canUpload} />
          ))}
        </div>
      )}

      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen}
        patientId={patientId} device={device} onUploaded={load} />
      <PreviewDialog open={!!previewRec} onOpenChange={(o) => !o && setPreviewRec(null)}
        patientId={patientId} rec={previewRec} />
    </div>
  );
}
